import { fetchAuthSession } from '@aws-amplify/auth';
import { SignatureV4 } from '@aws-sdk/signature-v4';
import { HttpRequest } from '@aws-sdk/protocol-http';
import { Sha256 } from '@aws-crypto/sha256-js';
import { appConfig } from '../config/appConfig';

export interface Citation {
  number: number;
  text: string;
  source: string;
  score: number;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  citations?: Citation[];
  messageId?: string;
  isWelcome?: boolean;
}

export interface RetrievalConfig {
  numberOfResults?: number;
  searchType?: 'HYBRID' | 'SEMANTIC';
  scoreThreshold?: number;
}

export async function* streamChat(
  query: string,
  knowledgeBaseId: string,
  modelId: string,
  apiUrl: string,
  retrievalConfig?: RetrievalConfig,
  systemPrompt?: string
): AsyncGenerator<{ type: 'content' | 'citations' | 'error'; data: any }> {
  // Get AWS credentials from Cognito
  const session = await fetchAuthSession();
  const credentials = session.credentials;

  if (!credentials) {
    throw new Error('No credentials available');
  }

  // Parse URL
  const url = new URL(apiUrl);
  const body = JSON.stringify({ query, knowledgeBaseId, modelId, retrievalConfig, systemPrompt });

  // Create HTTP request
  const request = new HttpRequest({
    method: 'POST',
    protocol: url.protocol.slice(0, -1),
    hostname: url.hostname,
    path: url.pathname,
    headers: {
      'Content-Type': 'application/json',
      host: url.hostname,
    },
    body,
  });

  // Sign request with AWS Signature V4
  const signer = new SignatureV4({
    credentials,
    region: appConfig.aws.region,
    service: 'lambda',
    sha256: Sha256,
  });

  const signedRequest = await signer.sign(request);

  // Make signed request
  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: signedRequest.headers as HeadersInit,
    body,
  });

  if (!response.ok || !response.body) {
    throw new Error('Failed to fetch');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (line.trim()) {
        try {
          const parsed = JSON.parse(line);
          yield parsed;
        } catch (e) {
          console.error('Parse error:', e);
        }
      }
    }
  }
}
